import { Ease, TimelineLite, TweenMax, TimelineMax, Linear } from "gsap";
import * as ScrollMagic from "scrollmagic";
import { ScrollMagicPluginGsap } from "scrollmagic-plugin-gsap";

import Debug from "debug";
import StartEventParameters = ScrollMagic.StartEventParameters;

ScrollMagicPluginGsap(ScrollMagic, TweenMax, TimelineMax);

const debug = Debug("cs.SessionAnimationController");

const inlineAnimationDurationAsPercentageOfViewportHeight = 30;
const desiredPositionAsPercentageOfViewport =
  100 - inlineAnimationDurationAsPercentageOfViewportHeight;
const immediateScrollDurationSeconds = 0.1;
const smoothScrollDurationSeconds = 1.5;
const quickScrollDurationSeconds = 0.5;

export interface TimelineQueueItem {
  shouldPlayInReverse: boolean;
  timelineId: string;
  id: number;
}

export enum SessionLayoutType {
  Wide,
  Inline,
}

export enum AutoScrollingMode {
  None = 0,
  Smooth = 1,
  Immediate = 2,
  Quick = 3,
}

interface ScrollInProgress {
  sceneIndex: number;
  scrollMode: AutoScrollingMode;
  tween: TweenMax;
  promise: Promise<void>;
}

function getViewportHeight() {
  return Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
}

function waitForScrollToComplete(
  desiredScrollPosition: number,
  scrollingElement: HTMLElement,
): Promise<void> {
  let limit = 0;
  return new Promise((resolve, reject) => {
    const intervalMethod = setInterval(() => {
      const calculatedValue = Math.min(
        Math.round(desiredScrollPosition),
        scrollingElement.scrollHeight - getViewportHeight(),
      );
      limit += 1;
      if (calculatedValue === scrollingElement.scrollTop || limit === 8) {
        resolve();
        clearInterval(intervalMethod);
      }
    }, 100);
  });
}

export class SessionAnimationController {
  public readonly scrollController: ScrollMagic.Controller;

  private readonly layoutType: SessionLayoutType;

  private readonly onSceneChangeCallback: (newSceneIndex: number) => void;

  private readonly scrollingElement: HTMLElement;

  private lastStartedScene: ScrollMagic.Scene | undefined;

  private scenes: ScrollMagic.Scene[] = [];

  private timelineQueue: TimelineQueueItem[] = [];

  private timelineCache: { [id: string]: TimelineLite } = {};

  private timelineMap: { [id: string]: () => TimelineLite } = {};

  private elementsToScrollTo: { [sceneId: number]: HTMLElement } = {};

  private currentAutoScroll: ScrollInProgress | undefined;

  private currentlyPlayingItem: TimelineQueueItem | undefined;

  constructor(
    layoutType: SessionLayoutType,
    scrollingElement: HTMLElement,
    onSceneChangeCallback: (newSceneIndex: number) => void,
  ) {
    debug("constructor");
    this.layoutType = layoutType;
    this.scrollingElement = scrollingElement;

    this.onSceneChangeCallback = onSceneChangeCallback;

    this.scrollController = new ScrollMagic.Controller({
      container: this.scrollingElement,
    } as ScrollMagic.ControllerOptions);
  }

  public destroy(): void {
    if (this.scrollController) {
      this.scrollController.destroy(true);
    }

    Object.keys(this.timelineCache).forEach((key) => {
      this.timelineCache[key].kill();
    });
  }

  public linkAnimationToScene(
    name: string,
    animation: () => TimelineLite | TimelineMax,
    scene: ScrollMagic.Scene,
    additionalOnStart?: (event: StartEventParameters) => void,
    elementToScrollTo?: HTMLElement,
    onSceneCallBack?: (event: StartEventParameters) => void,
  ): void {
    // Use a different approach depending on the navbars
    if (this.layoutType === SessionLayoutType.Wide) {
      this.linkAnimationToSceneWideLayout(
        name,
        animation,
        scene,
        additionalOnStart,
        elementToScrollTo,
        onSceneCallBack,
      );
    } else {
      this.linkAnimationToSceneInlineLayout(
        animation,
        scene,
        additionalOnStart,
        elementToScrollTo,
        onSceneCallBack,
      );
    }
  }

  public nextSlide(): Promise<void> {
    const currentIndex = this.getCurrentSceneIndex();
    if (currentIndex < this.scenes.length - 1) {
      return this.goToScene(currentIndex + 1, AutoScrollingMode.Smooth);
    }
    return Promise.resolve();
  }

  public previousSlide(): Promise<void> {
    const currentIndex = this.getCurrentSceneIndex();
    if (currentIndex >= 0) {
      return this.goToScene(currentIndex - 1, AutoScrollingMode.Smooth);
    }
    return Promise.resolve();
  }

  public goStraightToScene(sceneIndex: number): Promise<void> {
    return this.goToScene(sceneIndex, AutoScrollingMode.Immediate);
  }

  public handleWheelScroll = (delta: number): void => {
    const time = 0.4;
    const distance = 200;
    const offset = distance * delta;
    debug(`Wheel scrolling by ${offset}`);
    TweenMax.to(this.scrollingElement, time, {
      scrollTop: this.scrollingElement.scrollTop - offset,
    });
  };

  public scrollToTop(): Promise<void> {
    return new Promise((resolve) => {
      debug("scrollToTop");
      this.scrollingElement.scrollTop = 0;
      debug("scrollToTop end", this.scrollingElement.scrollTop);
      waitForScrollToComplete(0, this.scrollingElement).then(resolve);
    });
  }

  public scrollToInitialPosition(): void {
    const triggerElement = this.scenes[0].triggerElement() as HTMLElement;
    const viewportHeight = getViewportHeight();
    const triggerTop = triggerElement.getBoundingClientRect().top;
    this.scrollingElement.scrollTop = Math.max(0, triggerTop - viewportHeight - 10); // Just before the trigger
    debug("scrollToInitialPosition", this.scrollingElement.scrollTop);
  }

  public addIndicators(): void {
    this.scenes.forEach((s) => {
      // We need to check that the addIndicators plugin is actually available.
      if (s.addIndicators) {
        s.addIndicators({});
      }
    });
  }

  public goToScene(sceneIndex: number, scrollMode: AutoScrollingMode): Promise<void> {
    debug(`goToScene called with sceneIndex: ${sceneIndex}, scrollMode: ${scrollMode}`);
    if (!this.currentAutoScroll) {
      const currentScroll: any = {
        sceneIndex,
        scrollMode,
      };
      currentScroll.promise = new Promise<void>((resolve) => {
        const scrollSettings = this.getSettingsForScrollMode(scrollMode);
        const desiredScrollPosition = this.getDesiredScrollPositionForScene(sceneIndex);
        debug(
          `goToScene starting, from: ${this.scrollingElement.scrollTop}, to: ${desiredScrollPosition}, duration: ${scrollSettings.duration}`,
        );
        // We're actually animating "this" rather than the body, and using a property,  because the
        // relevant properties for scroll on the body are read-only, and you need to use
        // scrollTo instead.
        currentScroll.tween = TweenMax.to(this.scrollingElement, scrollSettings.duration, {
          ease: scrollSettings.ease,
          scrollTop: desiredScrollPosition,
          onComplete: () => {
            waitForScrollToComplete(desiredScrollPosition, this.scrollingElement).then(() =>
              resolve(),
            );
          },
        });
      }).finally(() => {
        debug("goToScene completed");
        this.currentAutoScroll = undefined;
      });
      this.currentAutoScroll = currentScroll;
      debug(`goToScene (new) sceneIndex: ${sceneIndex}, scrollMode: ${scrollMode}`, currentScroll);
    } else {
      // Ignore while there is a scroll in progress
    }

    return this.currentAutoScroll && this.currentAutoScroll.promise
      ? this.currentAutoScroll.promise
      : Promise.resolve();
  }

  private getCurrentSceneIndex() {
    return this.lastStartedScene ? this.scenes.indexOf(this.lastStartedScene) : -1;
  }

  private getSettingsForScrollMode(scrollMode: AutoScrollingMode): {
    ease: Ease | undefined;
    duration: number;
  } {
    switch (scrollMode) {
      case AutoScrollingMode.Immediate:
        return {
          ease: undefined,
          duration: immediateScrollDurationSeconds,
        };
      case AutoScrollingMode.Smooth:
        return {
          ease: undefined,
          duration: smoothScrollDurationSeconds,
        };
      case AutoScrollingMode.Quick:
        return {
          ease: Linear.easeNone,
          duration: quickScrollDurationSeconds,
        };
      default:
        throw new Error(`Unexpected scroll mode for scroll duration: ${scrollMode}`);
    }
  }

  private linkAnimationToSceneWideLayout(
    name: string,
    animation: () => TimelineLite,
    scene: ScrollMagic.Scene,
    additionalOnStart?: (event: StartEventParameters) => void,
    elementToScrollTo?: HTMLElement,
    onSceneCallBack?: (event: StartEventParameters) => void,
  ) {
    // Rather than triggering the animation directly, we use a queuing
    // mechanism to ensure we can deal with the entire set of transitions smoothly
    // (and avoid them overwriting each other!).

    // Add the animation to a map so that it can be triggered by name later
    this.timelineMap[name] = animation;

    // scene.addIndicators moved to this.addIndicators which is called after a final update to work around a chrome oddity.

    // Queue the triggering of the animation based on the start of the ScrollMagic scene
    scene
      .on("start", (event: StartEventParameters) => {
        if (additionalOnStart) {
          additionalOnStart(event);
        }
        this.triggerTimeline(name, event);

        if (onSceneCallBack) {
          onSceneCallBack(event);
        }
      })
      .addTo(this.scrollController);

    // Store the scene in an array so that we can use it to keep track of the 'current scene'
    // (to allow navigation by keyboard rather than scrolling)
    this.scenes.push(scene);

    // Define a dedicated element to scroll to when required (rather than just using the scene's trigger element)
    if (elementToScrollTo) {
      const sceneIndex = this.scenes.length - 1;
      this.elementsToScrollTo[sceneIndex] = elementToScrollTo;
    }
  }

  private linkAnimationToSceneInlineLayout(
    animation: () => TimelineLite,
    scene: ScrollMagic.Scene,
    additionalOnStart?: (event: StartEventParameters) => void,
    elementToScrollTo?: HTMLElement,
    onSceneCallBack?: (event: StartEventParameters) => void,
  ) {
    // In the inline method we give the ScrollMagic scene a scroll-duration,
    // so that animations transition in step with the scroll position

    scene.duration(`${inlineAnimationDurationAsPercentageOfViewportHeight}%`);

    // Allow ScrollMagic to control the animation directly
    const animationInstance = animation();
    animationInstance.paused(false);
    scene.setTween(animationInstance);
    // (scene as any).tween = animationInstance;
    // scene.addIndicators moved to this.addIndicators which is called after a final update to work around a chrome oddity.

    scene
      .on("start", (event: StartEventParameters) => {
        if (additionalOnStart) {
          additionalOnStart(event);
        }

        if (onSceneCallBack) {
          onSceneCallBack(event);
        }

        // Notify that we've changed scene
        this.onSceneChange(event);
      })
      .addTo(this.scrollController);

    // Store the scene in an array so that we can use it to keep track of the 'current scene'
    // (to allow navigation by keyboard rather than scrolling)
    this.scenes.push(scene);
    // (window as any).scenes = this.scenes;

    // Define a dedicated element to scroll to when required (rather than just using the scene's trigger element)
    if (elementToScrollTo) {
      const sceneIndex = this.scenes.length - 1;
      this.elementsToScrollTo[sceneIndex] = elementToScrollTo;
    }
  }

  private onSceneChange(event: StartEventParameters) {
    if (event.scrollDirection !== "REVERSE") {
      this.lastStartedScene = event.target;
    } else {
      const sceneIndex = this.scenes.indexOf(event.target);
      this.lastStartedScene = sceneIndex > 0 ? this.scenes[sceneIndex - 1] : undefined;
    }

    // Notify our parent of progress, so that it can be used to re-instantiate if we reset (e.g. on window resize)
    this.onSceneChangeCallback(
      this.lastStartedScene ? this.scenes.indexOf(this.lastStartedScene) : -1,
    );
  }

  private getDesiredScrollPositionForScene(sceneIndex: number) {
    if (sceneIndex === -1) {
      return 0;
    }

    const scene = this.scenes[sceneIndex];

    const usingExplicitElement = !!this.elementsToScrollTo[sceneIndex];

    if (!scene && !usingExplicitElement) {
      return 0; // Dan once got a sceneindex of 35 on SoloReport, out of range. This caused an error and stopped the page. So we have a guard here.
    }

    let triggerElement =
      this.elementsToScrollTo[sceneIndex] || (scene.triggerElement() as HTMLElement);

    // Handle a special case where we want to use the slide-inner as the trigger (to allow it to be moved offscreen),
    // but use the session-slide parent as the element to scroll to
    if (triggerElement.classList.contains("slide-inner") && !usingExplicitElement) {
      const slide = triggerElement.closest(".session-slide");
      if (slide) {
        triggerElement = slide as HTMLElement;
      }
    }

    if (this.layoutType === SessionLayoutType.Inline && !usingExplicitElement) {
      // Offset by the position in the viewport where the animation will complete
      const viewportHeight = getViewportHeight();
      const desiredScrollPosition = Math.max(
        triggerElement.offsetTop - (desiredPositionAsPercentageOfViewport / 100) * viewportHeight,
        0,
      );
      debug("getDesiredScrollPositionForScene default inline", {
        desiredScrollPosition,
        triggerElement,
      });
      return desiredScrollPosition;
    }
    debug("getDesiredScrollPositionForScene", {
      desiredScrollPosition: triggerElement.offsetTop,
      triggerElement,
    });
    return triggerElement.offsetTop;
  }

  private triggerTimeline(timelineId: string, event: StartEventParameters) {
    this.onSceneChange(event);
    const randomId = Math.floor(Math.random() * 1000);
    debug(`Trigger ${timelineId}, ${event.scrollDirection} (id: ${randomId})`);
    const queueItem: TimelineQueueItem = {
      shouldPlayInReverse: event.scrollDirection === "REVERSE",
      timelineId,
      id: randomId,
    };

    // Add the timeline to the queue
    this.timelineQueue.push(queueItem);

    // If an item is already playing and there's a queue building, speed it up
    if (this.currentlyPlayingItem && this.timelineQueue.length > 1) {
      const currentlyPlayingTimeline = this.getTimelineById(
        this.currentlyPlayingItem.timelineId,
        true,
      );
      currentlyPlayingTimeline.timeScale(5);
    }

    // If there isn't already an item playing, start playing the next item in the queue
    if (!this.currentlyPlayingItem) {
      this.startTimeline(this.timelineQueue[0]);
    }
  }

  private startTimeline(queueItem: TimelineQueueItem) {
    if (this.currentlyPlayingItem) {
      // We shouldn't arrive here if the animations are correctly triggered one at a time
      throw new Error("Animation is currently playing: cannot start another");
    }

    this.currentlyPlayingItem = queueItem;

    const timeline = this.getTimelineById(queueItem.timelineId);
    debug("startTimeline", timeline);
    this.resetTimelineState(timeline);

    if (
      this.timelineQueue.length > 1 ||
      (this.currentAutoScroll &&
        this.currentAutoScroll.scrollMode === AutoScrollingMode.Immediate) ||
      timeline.duration() === 0
    ) {
      this.seekTimeline(queueItem, timeline);
    } else {
      this.playTimeline(queueItem, timeline);
    }
  }

  private seekTimeline(queueItem: TimelineQueueItem, timeline: TimelineLite) {
    debug(
      `Seeking ${queueItem.timelineId} from queue ${
        queueItem.shouldPlayInReverse ? " in reverse" : ""
      } (id:${queueItem.id})`,
    );
    timeline.seek(queueItem.shouldPlayInReverse ? 0 : 999, true);
    this.onTimelineFinished(queueItem);
  }

  private playTimeline(queueItem: TimelineQueueItem, timeline: TimelineLite, timescale?: number) {
    debug(
      `Playing ${queueItem.timelineId} from queue ${
        queueItem.shouldPlayInReverse ? " in reverse" : ""
      } (id:${queueItem.id})`,
    );
    if (timescale) {
      timeline.timeScale(timescale);
    }
    if (queueItem.shouldPlayInReverse) {
      timeline.eventCallback("onReverseComplete", () => {
        this.onTimelineFinished(queueItem);
      });
      timeline.reverse(0, false);
    } else {
      timeline.eventCallback("onComplete", () => {
        this.onTimelineFinished(queueItem);
      });
      timeline.restart();
    }
  }

  private onTimelineFinished = (queueItem: TimelineQueueItem) => {
    debug(
      `${queueItem.timelineId}${queueItem.shouldPlayInReverse ? " reverse" : ""} finished (id:${
        queueItem.id
      })`,
    );

    // Remove the event handlers to avoid them being fired inappropriately later (e.g. on seek)
    this.clearEventsFromTimeline(queueItem.timelineId);

    if (this.currentlyPlayingItem?.id !== queueItem.id) {
      // Ignore the event
      debug("Ignoring timeline finished event", {
        current: this.currentlyPlayingItem,
        item: queueItem,
      });
      return;
    }

    if (this.currentlyPlayingItem?.id === queueItem.id) {
      this.currentlyPlayingItem = undefined;
    } else {
      throw new Error(
        "Unexpected currently playing item. Have multiple items been triggered simultaneously?",
      );
    }

    // Assume only a single animation at a time, so the front of the queue must be be the one
    if (this.timelineQueue[0]?.id === queueItem.id) {
      this.timelineQueue.shift();
    } else {
      throw new Error("Animations played out of order");
    }

    if (this.timelineQueue.length > 0) {
      // Start the next one
      this.startTimeline(this.timelineQueue[0]);
    }
  };

  private clearEventsFromTimeline(timelineId: string) {
    const timeline = this.getTimelineById(timelineId, true);
    timeline.eventCallback("onComplete", () => {
      /* Nothing - typings don't allow null */
    });
    timeline.eventCallback("onReverseComplete", () => {
      /* Nothing - typings don't allow null */
    });
  }

  private resetTimelineState(timeline: TimelineLite) {
    timeline.timeScale(1);
    timeline.reversed(false);
    timeline.pause();
  }

  private getTimelineById(timelineId: string, cachedOnly = false) {
    if (cachedOnly || this.timelineCache[timelineId]) {
      return this.timelineCache[timelineId];
    }
    this.timelineCache[timelineId] = this.timelineMap[timelineId]();
    return this.timelineCache[timelineId];
  }
}
